import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/router";
import { useInstructor } from "@/hooks/useInstructor";
import { formatDate as formatDateUtil } from "@/utils/formatDate";

const InstructorNotifications = ({ userId }) => {
  const router = useRouter();
  const [overlayOpen, setOverlayOpen] = useState(false);
  const notificationRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const {
    enrollmentNotifications,
    enrollmentNotificationCount,
    isLoadingEnrollmentNotifications,
    fetchEnrollmentNotifications,
    markEnrollmentNotificationAsRead,
  } = useInstructor(userId, "instructor");

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideNotification =
        notificationRef.current &&
        !notificationRef.current.contains(event.target);

      if (isOutsideNotification) {
        setOverlayOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!userId) return;

    const intervalId = setInterval(() => {
      fetchEnrollmentNotifications();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [userId, fetchEnrollmentNotifications]);

  const handleNotificationMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    if (enrollmentNotificationCount > 0) {
      setOverlayOpen(true);

      if (
        enrollmentNotifications.length === 0 ||
        enrollmentNotificationCount > 0
      ) {
        fetchEnrollmentNotifications();
      }
    }
  };

  const handleNotificationMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setOverlayOpen(false);
    }, 200);
  };

  const handleOverlayMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handleOverlayMouseLeave = () => {
    setOverlayOpen(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDateUtil(date);
  };

  const handleEnrollmentNotificationClick = async (notification) => {
    await markEnrollmentNotificationAsRead(notification._id);
    await fetchEnrollmentNotifications();
    setOverlayOpen(false);

    if (notification.type === 'COURSE_ENROLLMENT' && notification.referenceId) {
      router.push(`/course-details/${notification.referenceId}`);
    } else if (notification.type === 'EVENT_ENROLLMENT' && notification.referenceId) {
      router.push(`/event-details/${notification.referenceId}`);
    }
  };

  const renderNotificationOverlay = () => {
    const hasEnrollments =
      enrollmentNotificationCount > 0 ||
      enrollmentNotifications.filter((n) => !n.isRead).length > 0;

    if (!hasEnrollments) return null;

    return (
      <div
        className="rbt-job-notification-overlay"
        onMouseEnter={handleOverlayMouseEnter}
        onMouseLeave={handleOverlayMouseLeave}
      >
        <div className="rbt-notification-section">
          <div className="rbt-job-overlay-header">
            <h6 className="rbt-job-overlay-title">New Enrollments</h6>
          </div>

          {isLoadingEnrollmentNotifications ? (
            <div className="rbt-job-overlay-loading">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span className="ms-2">Loading...</span>
            </div>
          ) : enrollmentNotifications.filter((n) => !n.isRead).length > 0 ? (
            <ul className="rbt-job-overlay-list">
              {enrollmentNotifications
                .filter((notification) => !notification.isRead)
                .map((notification) => (
                  <li
                    key={notification._id}
                    className="rbt-job-overlay-item rbt-selection-notification unread"
                    onClick={() => handleEnrollmentNotificationClick(notification)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="rbt-job-overlay-content">
                      <div className="rbt-selection-notification-header">
                        <div className="rbt-job-overlay-title-text">
                          <strong>{notification.title}</strong>&nbsp;
                          <span className="rbt-job-overlay-time">
                            ({formatDate(notification.createdAt)})
                          </span>
                        </div>
                      </div>
                      <div className="rbt-job-overlay-company">
                        {notification.message}
                      </div>
                    </div>
                  </li>
                ))}
            </ul>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <>
      <style jsx>{`
        .rbt-notification-section {
          padding: 0;
        }

        .rbt-selection-notification {
          transition: background-color 0.2s;
        }

        .rbt-selection-notification.unread {
          background-color: #f0f8ff;
        }

        .rbt-selection-notification:hover {
          background-color: #f5f5f5;
        }

        .rbt-selection-notification-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 4px;
        }

        .rbt-job-notification-overlay {
          max-height: 500px;
          overflow-y: auto;
        }

        .rbt-job-overlay-content {
          display: flex;
          flex-direction: column;
        }
      `}</style>

      <li
        className="access-icon rbt-notification-wrapper rbt-notification-position"
        ref={notificationRef}
      >
        <Link
          className="rbt-round-btn rbt-notification-btn"
          href="/notifications"
          onMouseEnter={handleNotificationMouseEnter}
          onMouseLeave={handleNotificationMouseLeave}
          onClick={(e) => {
            router.push('/notifications');
          }}
          title="Notifications"
        >
          <i className="feather-bell"></i>
          {enrollmentNotificationCount > 0 && (
            <span className="rbt-notification-count">
              {enrollmentNotificationCount > 99 ? "99+" : enrollmentNotificationCount}
            </span>
          )}
        </Link>
        {overlayOpen && renderNotificationOverlay()}
      </li>
    </>
  );
};

export default InstructorNotifications;